// Crawlable catalog facts for every card page (#753).
// These helpers are pure so #743 can render the same section into the first HTML
// response. JSON-LD here is Product + additionalProperty only; #747 adds Offer,
// BreadcrumbList, Organization, and ItemList without rebuilding these facts.

export type CatalogLocale = 'ja' | 'en'

export type CatalogTranslate = (key: string) => string

export type CatalogValue = {
  text: string
  href?: string
}

export type CatalogBlock = {
  name?: string
  meta?: string
  text?: string
}

export type CatalogRow = {
  propertyId: string
  label: string
  values?: CatalogValue[]
  blocks?: CatalogBlock[]
}

export type PresentedCardCatalog = {
  itemId: number
  name: string
  heading: string
  separator: string
  description?: string
  intro?: string
  rows: CatalogRow[]
  images: string[]
  jsonLd?: Record<string, unknown>
}

export type CardCatalogImage = {
  imageUrl?: string
  image_url?: string
  standard?: string
  sprite?: boolean
}

export type CardCatalogTag = {
  id?: number
  type?: string
  parentId?: number
  parent_id?: number
  nameEn?: string
  nameJa?: string
  name_en?: string
  name_ja?: string
  ordinality?: number
}

export type CardCatalogSource = {
  id: number
  nameEn?: string
  nameJa?: string
  name_en?: string
  name_ja?: string
  description?: string
  images?: CardCatalogImage[]
  tags?: CardCatalogTag[]
  itemData?: { body?: unknown, flavorText?: string }
  item_data?: { body?: unknown, flavor_text?: string }
  itemDatas?: Array<{ body?: unknown, flavorText?: string }>
  item_datas?: Array<{ body?: unknown, flavor_text?: string }>
  holicListingsCount?: number
  holic_listings_count?: number
  holicMinimumPrice?: number
  holic_minimum_price?: number
}

export type PresentCardCatalogOptions = {
  translate?: CatalogTranslate
  pageUrl?: string
}

type NamedLink = {
  text: string
  href?: string
}

type AttackFact = {
  name: string
  damage?: string
  cost: string[]
  text?: string
}

type AbilityFact = {
  name?: string
  text?: string
}

type NormalizedTag = {
  id?: number
  parentId?: number
  type: string
  nameJa?: string
  nameEn?: string
  ordinality?: number
}

type CatalogFacts = {
  itemId: number
  name: string
  description?: string
  images: string[]
  sets: NamedLink[]
  number?: string
  rarities: NamedLink[]
  regulation?: string
  supertype?: string
  subtypes: NamedLink[]
  types: NamedLink[]
  hp?: string
  level?: string
  illustrators: NamedLink[]
  characters: NamedLink[]
  pokedex: string[]
  evolvesFrom?: string
  attacks: AttackFact[]
  abilities: AbilityFact[]
  weaknesses: string[]
  resistances: string[]
  retreat?: string
  ancientTrait?: { name?: string, text?: string }
  rules: string[]
  flavor?: string
  gbPack?: string
  gb2Pack?: string
  wiki?: string
  listingsCount: number
  minPrice: number
}

const ENERGY_JA: Record<string, string> = {
  colorless: '無',
  grass: '草',
  fire: '炎',
  water: '水',
  lightning: '雷',
  psychic: '超',
  fighting: '闘',
  darkness: '悪',
  metal: '鋼',
  dragon: 'ドラゴン',
  fairy: 'フェアリー',
}

const SUPERTYPE_JA: Record<string, string> = {
  Pokémon: 'ポケモン',
  Pokemon: 'ポケモン',
  Trainer: 'トレーナーズ',
  Energy: 'エネルギー',
  'Special Card': 'スペシャルカード',
}

const ROW_LABELS: Record<string, { ja: string, en: string }> = {
  title: { ja: 'カードデータ', en: 'Card details' },
  set: { ja: '収録', en: 'Set' },
  number: { ja: '型番', en: 'Number' },
  rarity: { ja: 'レアリティ', en: 'Rarity' },
  regulation: { ja: 'レギュレーション', en: 'Regulation' },
  supertype: { ja: 'カードの種類', en: 'Supertype' },
  subtype: { ja: '分類', en: 'Classification' },
  type: { ja: 'タイプ', en: 'Type' },
  hp: { ja: 'HP', en: 'HP' },
  level: { ja: 'レベル', en: 'Level' },
  illustrator: { ja: 'イラストレーター', en: 'Illustrator' },
  character: { ja: 'ポケモン', en: 'Pokémon' },
  pokedex: { ja: '図鑑番号', en: 'Pokédex' },
  evolvesFrom: { ja: '進化元', en: 'Evolves from' },
  attack: { ja: '技', en: 'Attacks' },
  ability: { ja: '特性', en: 'Abilities' },
  weakness: { ja: '弱点', en: 'Weakness' },
  resistance: { ja: '抵抗力', en: 'Resistance' },
  retreat: { ja: 'にげる', en: 'Retreat' },
  ancientTrait: { ja: '古代能力', en: 'Ancient trait' },
  rule: { ja: 'ルール', en: 'Rules' },
  flavorText: { ja: 'フレーバー', en: 'Flavor text' },
  gbPack: { ja: 'GBパック', en: 'GB pack' },
  gb2Pack: { ja: 'GB2パック', en: 'GB2 pack' },
  wiki: { ja: '参考', en: 'Reference' },
}

const FLAVOR_INTRO_LIMIT = 80

const catalogLocale = (language: string | undefined): CatalogLocale => {
  return language?.toLowerCase().startsWith('en') ? 'en' : 'ja'
}

const clean = (value: unknown): string => {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value !== 'string') return ''
  return value.replace(/\u3000/g, ' ').trim()
}

const cleanOrUndefined = (value: unknown): string | undefined => {
  const text = clean(value)
  return text || undefined
}

const codePointLength = (value: string): number => Array.from(value).length

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return
  return value as Record<string, unknown>
}

const asArray = (value: unknown): unknown[] => Array.isArray(value) ? value : []

const meaningfulText = (value: unknown): string | undefined => {
  const text = clean(value)
  if (!text) return
  if (/^[-–—－×xX+＋.。・\s]+$/.test(text)) return
  return text
}

const meaningfulDamage = (value: unknown): string | undefined => {
  const text = clean(value)
  if (!text || !/\d/.test(text)) return
  return text
}

const field = (body: Record<string, unknown>, ...keys: string[]): unknown => {
  for (const key of keys) {
    const value = body[key]
    if (value === undefined || value === null) continue
    if (typeof value === 'string' && !clean(value)) continue
    return value
  }
}

const translated = (translate: CatalogTranslate | undefined, key: string, fallback: string): string => {
  if (!translate) return fallback
  const raw = translate(key)
  if (typeof raw !== 'string') return fallback
  const value = clean(raw)
  if (!value || value === key) return fallback
  return value
}

const rowLabel = (propertyId: string, locale: CatalogLocale, translate?: CatalogTranslate): string => {
  const fallback = ROW_LABELS[propertyId]?.[locale] ?? propertyId
  return translated(translate, `cards.catalog.${propertyId}`, fallback)
}

const energyLabel = (code: string, locale: CatalogLocale, translate?: CatalogTranslate): string => {
  const normalized = clean(code)
  if (!normalized) return ''
  const fallback = locale === 'ja' ? (ENERGY_JA[normalized.toLowerCase()] ?? normalized) : normalized
  return translated(translate, `cards.types.${normalized.toLowerCase()}`, fallback)
}

const supertypeLabel = (code: string, locale: CatalogLocale, translate?: CatalogTranslate): string => {
  const normalized = clean(code)
  if (!normalized) return ''
  const fallback = locale === 'ja' ? (SUPERTYPE_JA[normalized] ?? normalized) : normalized
  return translated(translate, `cards.catalog.supertypes.${normalized}`, fallback)
}

const layoutLabel = (code: string, locale: CatalogLocale, translate?: CatalogTranslate): string => {
  const normalized = clean(code)
  if (!normalized) return ''
  return translated(translate, `cards.layouts.${normalized}`, normalized)
}

const rarityLabel = (code: string, locale: CatalogLocale, translate?: CatalogTranslate): string => {
  const normalized = clean(code)
  if (!normalized) return ''
  return translated(translate, `cards.rarities.${normalized}`, normalized)
}

const codedLabel = (
  kind: 'energy' | 'layout' | 'rarity',
  code: string,
  locale: CatalogLocale,
  translate?: CatalogTranslate,
): string => {
  if (kind === 'energy') return energyLabel(code, locale, translate)
  if (kind === 'layout') return layoutLabel(code, locale, translate)
  return rarityLabel(code, locale, translate)
}

const localizedName = (ja: unknown, en: unknown, locale: CatalogLocale): string => {
  const primary = clean(locale === 'ja' ? ja : en)
  const secondary = clean(locale === 'ja' ? en : ja)
  return primary || secondary
}

const positiveCount = (value: unknown): number => {
  const count = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(count) || count <= 0) return 0
  return Math.floor(count)
}

const positivePrice = (value: unknown): number => {
  const price = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(price) || price <= 0) return 0
  return Math.round(price)
}

const categoryHref = (id: number | undefined): string | undefined => {
  if (!id || id <= 0) return
  return `/categories/${id}`
}

const safeHttpUrl = (value: unknown): string | undefined => {
  const text = clean(value)
  if (!text) return
  try {
    const url = new URL(text)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return
    return url.toString()
  } catch {
    return
  }
}

const safeHref = (href: string | undefined): string | undefined => {
  if (!href) return
  if (href.startsWith('/categories/')) {
    return /^\d+$/.test(href.slice('/categories/'.length)) ? href : undefined
  }
  return safeHttpUrl(href)
}

const canonicalPageUrl = (pageUrl: string | undefined): string | undefined => {
  if (!pageUrl) return
  try {
    const url = new URL(pageUrl)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return
    url.search = ''
    url.hash = ''
    return url.toString()
  } catch {
    return
  }
}

const absoluteUrl = (pageUrl: string | undefined, href: string | undefined): string | undefined => {
  const safe = safeHref(href)
  if (!safe) return
  if (safe.startsWith('http')) return safe
  const base = canonicalPageUrl(pageUrl)
  if (!base) return
  try {
    return new URL(safe, base).toString()
  } catch {
    return
  }
}

const readBody = (source: CardCatalogSource): Record<string, unknown> => {
  const candidates = [
    source.itemData?.body,
    source.item_data?.body,
    source.itemDatas?.[0]?.body,
    source.item_datas?.[0]?.body,
  ]
  for (const candidate of candidates) {
    const record = asRecord(candidate)
    if (record && Object.keys(record).length > 0) return record
  }
  return {}
}

const readImages = (source: CardCatalogSource): string[] => {
  const urls: string[] = []
  for (const image of source.images ?? []) {
    if (!image || image.sprite) continue
    const url = clean(image.standard) || clean(image.imageUrl) || clean(image.image_url)
    if (url) urls.push(url)
  }
  return urls
}

const normalizeTags = (source: CardCatalogSource): NormalizedTag[] => {
  return (source.tags ?? []).flatMap((tag) => {
    const type = clean(tag.type)
    if (!type) return []
    const nameJa = cleanOrUndefined(tag.nameJa ?? tag.name_ja)
    const nameEn = cleanOrUndefined(tag.nameEn ?? tag.name_en)
    const parentId = tag.parentId ?? tag.parent_id
    const ordinality = typeof tag.ordinality === 'number' && Number.isFinite(tag.ordinality) ? tag.ordinality : undefined
    return [{
      id: typeof tag.id === 'number' ? tag.id : undefined,
      parentId: typeof parentId === 'number' ? parentId : undefined,
      type,
      nameJa,
      nameEn,
      ordinality,
    }]
  })
}

const leaves = (tags: NormalizedTag[]): NormalizedTag[] => {
  const parentIds = new Set(tags.flatMap((tag) => typeof tag.parentId === 'number' ? [tag.parentId] : []))
  const leafTags = tags.filter((tag) => typeof tag.id !== 'number' || !parentIds.has(tag.id))
  return leafTags.length > 0 ? leafTags : tags
}

const labelForTag = (
  tag: NormalizedTag,
  locale: CatalogLocale,
  translate: CatalogTranslate | undefined,
  kind: 'plain' | 'energy' | 'layout' | 'rarity',
): string => {
  const primary = locale === 'ja' ? tag.nameJa : tag.nameEn
  const secondary = locale === 'ja' ? tag.nameEn : tag.nameJa
  if (primary) return primary
  if (secondary && kind !== 'plain') {
    const translatedName = codedLabel(kind, secondary, locale, translate)
    if (translatedName) return translatedName
  }
  return secondary ?? ''
}

const namedLinks = (
  tags: NormalizedTag[],
  locale: CatalogLocale,
  translate: CatalogTranslate | undefined,
  kind: 'plain' | 'energy' | 'layout' | 'rarity',
): NamedLink[] => {
  const seen = new Set<string>()
  const links: NamedLink[] = []
  for (const tag of tags) {
    const text = labelForTag(tag, locale, translate, kind)
    if (!text) continue
    const href = categoryHref(tag.id)
    const key = `${href ?? ''}::${text}`
    if (seen.has(key)) continue
    seen.add(key)
    links.push(href ? { text, href } : { text })
  }
  return links
}

const linksFromTags = (
  tags: NormalizedTag[],
  locale: CatalogLocale,
  translate: CatalogTranslate | undefined,
  kind: 'plain' | 'energy' | 'layout' | 'rarity',
): NamedLink[] => namedLinks(leaves(tags), locale, translate, kind)

const isEditionName = (name: string | undefined): boolean => {
  if (!name) return false
  const normalized = name.trim().toLowerCase()
  return normalized === '初版'
    || normalized === '旧裏面'
    || normalized === 'unlimited'
    || normalized === 'shadowless'
    || normalized === '1st edition'
    || normalized.startsWith('edition ')
}

const isEditionTag = (tag: NormalizedTag): boolean => isEditionName(tag.nameJa) || isEditionName(tag.nameEn)

// Edition leaves (初版, Unlimited, Shadowless, Edition 1) sit under the set.
// Keep that set. Leave series and other non-edition leaves as the deepest tag only.
const categoryTagsForDisplay = (tags: NormalizedTag[]): NormalizedTag[] => {
  const byId = new Map<number, NormalizedTag>()
  for (const tag of tags) {
    if (typeof tag.id === 'number') byId.set(tag.id, tag)
  }
  const chosen: NormalizedTag[] = []
  const seen = new Set<number>()
  const push = (tag: NormalizedTag | undefined): void => {
    if (!tag || typeof tag.id !== 'number' || seen.has(tag.id)) return
    seen.add(tag.id)
    chosen.push(tag)
  }
  for (const tag of leaves(tags)) {
    if (isEditionTag(tag) && typeof tag.parentId === 'number') push(byId.get(tag.parentId))
    push(tag)
  }
  return chosen
}

const plainNames = (value: unknown): string[] => {
  const text = clean(value)
  if (!text) return []
  return text
    .split(/[,/+/、]/)
    .map((part) => part.trim())
    .filter((part) => part && !/^holic-\d+$/.test(part))
}

const seriesNames = (value: unknown): string[] => {
  const list = Array.isArray(value) ? value : (typeof value === 'string' ? [value] : [])
  return list.map(clean).filter((name) => name && !/^holic-\d+$/.test(name))
}

const pokedexNumbers = (value: unknown, characters: NormalizedTag[]): string[] => {
  const charactersById = new Map<number, NormalizedTag>()
  for (const tag of characters) {
    if (typeof tag.id === 'number') charactersById.set(tag.id, tag)
  }

  const seen = new Set<string>()
  const numbers: string[] = []
  for (const entry of asArray(value)) {
    const text = clean(entry)
    let number: string | undefined
    if (/^\d+$/.test(text)) {
      number = text
    } else {
      const match = /^holic-(\d+)$/.exec(text)
      const id = match ? Number(match[1]) : NaN
      const ordinality = Number.isFinite(id) ? charactersById.get(id)?.ordinality : undefined
      if (typeof ordinality === 'number' && ordinality > 0) number = String(ordinality)
    }
    if (!number || seen.has(number)) continue
    seen.add(number)
    numbers.push(number)
  }
  return numbers
}

const readAttacks = (
  value: unknown,
  locale: CatalogLocale,
  translate?: CatalogTranslate,
): AttackFact[] => {
  return asArray(value).flatMap((entry) => {
    const record = asRecord(entry)
    if (!record) return []
    const name = cleanOrUndefined(record.name)
    if (!name) return []
    const cost = asArray(record.cost)
      .map((code) => energyLabel(clean(code), locale, translate))
      .filter(Boolean)
    return [{
      name,
      damage: meaningfulDamage(record.damage),
      text: meaningfulText(record.text),
      cost,
    }]
  })
}

const readAbilities = (value: unknown): AbilityFact[] => {
  return asArray(value).flatMap((entry) => {
    const record = asRecord(entry)
    if (!record) return []
    const name = meaningfulText(record.name)
    const text = meaningfulText(record.text)
    if (!name && !text) return []
    return [{ name, text }]
  })
}

const readCombatPairs = (
  value: unknown,
  locale: CatalogLocale,
  translate?: CatalogTranslate,
): string[] => {
  return asArray(value).flatMap((entry) => {
    const record = asRecord(entry)
    if (!record) return []
    const typeName = clean(record.type)
    const amount = clean(record.value)
    const label = typeName ? energyLabel(typeName, locale, translate) : ''
    const line = [label, amount].filter(Boolean).join(' ')
    return line ? [line] : []
  })
}

const isPokemonSupertype = (value: string | undefined): boolean => {
  if (!value) return false
  return value === 'Pokémon' || value.toLowerCase() === 'pokemon'
}

const readRetreat = (
  body: Record<string, unknown>,
  locale: CatalogLocale,
  translate: CatalogTranslate | undefined,
  supertype: string | undefined,
): string | undefined => {
  const costs = asArray(field(body, 'retreatCost', 'retreat_cost')).map(clean).filter(Boolean)
  if (costs.length > 0) {
    return costs.map((code) => energyLabel(code, locale, translate)).filter(Boolean).join(locale === 'ja' ? '・' : ' · ')
  }
  const raw = field(body, 'convertedRetreatCost', 'converted_retreat_cost')
  if (typeof raw !== 'number' && typeof raw !== 'string') return
  const count = typeof raw === 'number' ? raw : Number(raw)
  if (!Number.isFinite(count) || count < 0) return
  if (count === 0) return isPokemonSupertype(supertype) ? '0' : undefined
  return String(Math.floor(count))
}

const readAncient = (value: unknown): { name?: string, text?: string } | undefined => {
  const record = asRecord(value)
  if (!record) return
  const name = meaningfulText(record.name)
  const text = meaningfulText(record.text)
  if (!name && !text) return
  return { name, text }
}

const readRules = (value: unknown): string[] => {
  const list = Array.isArray(value) ? value : (typeof value === 'string' ? [value] : [])
  return list.flatMap((rule) => {
    const text = meaningfulText(rule)
    return text ? [text] : []
  })
}

const readFlavor = (source: CardCatalogSource, body: Record<string, unknown>): string | undefined => {
  return meaningfulText(field(body, 'flavorText', 'flavor_text'))
    ?? meaningfulText(source.itemData?.flavorText)
    ?? meaningfulText(source.item_data?.flavor_text)
    ?? meaningfulText(source.itemDatas?.[0]?.flavorText)
    ?? meaningfulText(source.item_datas?.[0]?.flavor_text)
}

const collectFacts = (
  source: CardCatalogSource,
  locale: CatalogLocale,
  translate?: CatalogTranslate,
): CatalogFacts => {
  const body = readBody(source)
  const tags = normalizeTags(source)
  const tagsOf = (type: string): NormalizedTag[] => tags.filter((tag) => tag.type === type)

  const setLinks = namedLinks(categoryTagsForDisplay(tagsOf('Category')), locale, translate, 'plain')
  const sets = setLinks.length > 0
    ? setLinks
    : seriesNames(field(body, 'series')).map((text) => ({ text }))

  const typeLinks = linksFromTags(tagsOf('CardType'), locale, translate, 'energy')
  const types = typeLinks.length > 0
    ? typeLinks
    : asArray(field(body, 'types')).map(clean).filter(Boolean).map((code) => ({ text: energyLabel(code, locale, translate) }))

  const subtypeLinks = linksFromTags(tagsOf('CardLayout'), locale, translate, 'layout')
  const subtypes = subtypeLinks.length > 0
    ? subtypeLinks
    : asArray(field(body, 'subtypes')).map(clean).filter(Boolean).map((code) => ({ text: layoutLabel(code, locale, translate) }))

  const rarityLinks = linksFromTags(tagsOf('Rarity'), locale, translate, 'rarity')
  const rarityCode = cleanOrUndefined(field(body, 'rarity'))
  const rarities = rarityLinks.length > 0
    ? rarityLinks
    : (rarityCode ? [{ text: rarityLabel(rarityCode, locale, translate) }] : [])

  const illustratorLinks = linksFromTags(tagsOf('Illustrator'), locale, translate, 'plain')
  const illustrators = illustratorLinks.length > 0
    ? illustratorLinks
    : plainNames(field(body, 'artist')).map((text) => ({ text }))

  const level = cleanOrUndefined(field(body, 'level'))
  const evolves = cleanOrUndefined(field(body, 'evolvesFrom', 'evolves_from'))
  const supertype = cleanOrUndefined(field(body, 'supertype'))

  const name = localizedName(source.nameJa ?? source.name_ja, source.nameEn ?? source.name_en, locale)
    || clean(field(body, 'name'))
    || `#${source.id}`

  return {
    itemId: source.id,
    name,
    description: meaningfulText(source.description),
    images: readImages(source),
    sets,
    number: cleanOrUndefined(field(body, 'number')),
    rarities,
    regulation: cleanOrUndefined(field(body, 'regulation')),
    supertype: supertype ? supertypeLabel(supertype, locale, translate) : undefined,
    subtypes,
    types,
    hp: cleanOrUndefined(field(body, 'hp')),
    level: level && level !== '0' ? level : undefined,
    illustrators,
    characters: linksFromTags(tagsOf('Character'), locale, translate, 'plain'),
    pokedex: pokedexNumbers(field(body, 'nationalPokedexNumbers', 'national_pokedex_numbers'), tagsOf('Character')),
    evolvesFrom: evolves && !/^holic-\d+$/.test(evolves) ? evolves : undefined,
    attacks: readAttacks(field(body, 'attacks'), locale, translate),
    abilities: readAbilities(field(body, 'abilities')),
    weaknesses: readCombatPairs(field(body, 'weaknesses'), locale, translate),
    resistances: readCombatPairs(field(body, 'resistances'), locale, translate),
    retreat: readRetreat(body, locale, translate, supertype),
    ancientTrait: readAncient(field(body, 'ancientTrait', 'ancient_trait')),
    rules: readRules(field(body, 'rules')),
    flavor: readFlavor(source, body),
    gbPack: meaningfulText(field(body, 'gbPack', 'gb_pack')),
    gb2Pack: meaningfulText(field(body, 'gb2Pack', 'gb2_pack')),
    wiki: safeHttpUrl(field(body, 'wikiUrl', 'wiki_url')),
    listingsCount: positiveCount(source.holicListingsCount ?? source.holic_listings_count),
    minPrice: positivePrice(source.holicMinimumPrice ?? source.holic_minimum_price),
  }
}

const joinText = (links: NamedLink[], separator: string): string => links.map((link) => link.text).join(separator)

const extraCharacterNames = (facts: CatalogFacts): string[] => {
  return facts.characters.map((character) => character.text).filter((name) => name && !facts.name.includes(name))
}

const hasDistinguishingFacts = (facts: CatalogFacts): boolean => {
  const flavorIsShort = Boolean(facts.flavor && codePointLength(facts.flavor) <= FLAVOR_INTRO_LIMIT)
  return Boolean(
    facts.sets.length > 0 ||
    facts.number ||
    facts.rarities.length > 0 ||
    facts.regulation ||
    facts.subtypes.length > 0 ||
    facts.types.length > 0 ||
    facts.hp ||
    facts.level ||
    facts.illustrators.length > 0 ||
    extraCharacterNames(facts).length > 0 ||
    facts.pokedex.length > 0 ||
    facts.evolvesFrom ||
    facts.attacks.length > 0 ||
    facts.abilities.some((ability) => ability.name) ||
    facts.ancientTrait?.name ||
    flavorIsShort
  )
}

const listingSentence = (facts: CatalogFacts, locale: CatalogLocale): string => {
  if (facts.listingsCount <= 0) return ''
  if (locale === 'ja') {
    return facts.minPrice > 0
      ? `販売中の出品は${facts.listingsCount}件、最安${facts.minPrice}円。`
      : `販売中の出品は${facts.listingsCount}件。`
  }
  return facts.minPrice > 0
    ? ` ${facts.listingsCount} listed for sale on HOLIC, from ¥${facts.minPrice}.`
    : ` ${facts.listingsCount} listed for sale on HOLIC.`
}

const buildJaIntro = (facts: CatalogFacts): string => {
  const sep = '、'
  let identity = facts.sets.length > 0 ? `${joinText(facts.sets, sep)}の${facts.name}` : facts.name
  if (facts.number) identity += `（${facts.number}）`

  const traits: string[] = []
  if (facts.rarities.length > 0) traits.push(`レアリティ${joinText(facts.rarities, '・')}`)
  if (facts.regulation) traits.push(`レギュレーション${facts.regulation}`)
  if (facts.subtypes.length > 0) traits.push(joinText(facts.subtypes, '・'))
  if (facts.types.length > 0) traits.push(`${joinText(facts.types, '・')}タイプ`)
  if (facts.hp) traits.push(`HP${facts.hp}`)
  if (facts.level) traits.push(`レベル${facts.level}`)

  let intro = traits.length > 0 ? `${identity}は${traits.join(sep)}。` : `${identity}。`
  if (facts.illustrators.length > 0) intro += `イラストは${joinText(facts.illustrators, sep)}。`
  if (facts.attacks.length > 0) {
    const attacks = facts.attacks.map((attack) => attack.damage ? `${attack.name}（${attack.damage}）` : attack.name)
    intro += `技は${attacks.join(sep)}。`
  }
  const abilityNames = facts.abilities.flatMap((ability) => ability.name ? [ability.name] : [])
  if (abilityNames.length > 0) intro += `特性は${abilityNames.join(sep)}。`
  if (facts.evolvesFrom) intro += `${facts.evolvesFrom}から進化。`
  const characters = extraCharacterNames(facts)
  if (characters.length > 0) intro += `登場は${characters.join(sep)}。`
  if (facts.pokedex.length > 0) intro += `図鑑番号${facts.pokedex.join(sep)}。`
  if (facts.ancientTrait?.name) intro += `古代能力は${facts.ancientTrait.name}。`
  if (facts.flavor && codePointLength(facts.flavor) <= FLAVOR_INTRO_LIMIT) {
    intro += `「${facts.flavor}」`
    if (!/[。！？!?]$/.test(facts.flavor)) intro += '。'
  }
  intro += listingSentence(facts, 'ja')
  return intro
}

const buildEnIntro = (facts: CatalogFacts): string => {
  const sep = ', '
  let identity = facts.name
  if (facts.number) identity += ` (${facts.number})`
  if (facts.sets.length > 0) identity += ` from ${joinText(facts.sets, sep)}`

  const traits: string[] = []
  if (facts.rarities.length > 0) traits.push(joinText(facts.rarities, ' / '))
  if (facts.regulation) traits.push(`regulation ${facts.regulation}`)
  if (facts.subtypes.length > 0) traits.push(joinText(facts.subtypes, ' / '))
  if (facts.types.length > 0) traits.push(`${joinText(facts.types, '/')} type`)
  if (facts.hp) traits.push(`HP ${facts.hp}`)
  if (facts.level) traits.push(`LV.${facts.level}`)

  let intro = traits.length > 0 ? `${identity} — ${traits.join(sep)}.` : `${identity}.`
  if (facts.illustrators.length > 0) intro += ` Illustrated by ${joinText(facts.illustrators, sep)}.`
  if (facts.attacks.length > 0) {
    const attacks = facts.attacks.map((attack) => attack.damage ? `${attack.name} (${attack.damage})` : attack.name)
    intro += ` Attacks: ${attacks.join(sep)}.`
  }
  const abilityNames = facts.abilities.flatMap((ability) => ability.name ? [ability.name] : [])
  if (abilityNames.length > 0) intro += ` Abilities: ${abilityNames.join(sep)}.`
  if (facts.evolvesFrom) intro += ` Evolves from ${facts.evolvesFrom}.`
  const characters = extraCharacterNames(facts)
  if (characters.length > 0) intro += ` Features ${characters.join(sep)}.`
  if (facts.pokedex.length > 0) intro += ` Pokédex ${facts.pokedex.join(sep)}.`
  if (facts.ancientTrait?.name) intro += ` Ancient trait: ${facts.ancientTrait.name}.`
  if (facts.flavor && codePointLength(facts.flavor) <= FLAVOR_INTRO_LIMIT) {
    intro += ` “${facts.flavor}”`
    if (!/[.!?。！？]$/.test(facts.flavor)) intro += '.'
  }
  intro += listingSentence(facts, 'en')
  return intro
}

const buildIntro = (facts: CatalogFacts, locale: CatalogLocale): string | undefined => {
  if (!hasDistinguishingFacts(facts)) return
  const intro = locale === 'ja' ? buildJaIntro(facts) : buildEnIntro(facts)
  const remainder = intro.replace(facts.name, '').replace(/[。.\s“”「」]/g, '')
  return remainder ? intro : undefined
}

const attackMeta = (attack: AttackFact, locale: CatalogLocale): string | undefined => {
  const cost = attack.cost.join(locale === 'ja' ? '・' : ' · ')
  const meta = [attack.damage, cost].filter(Boolean).join(' ')
  return meta || undefined
}

const pushValues = (rows: CatalogRow[], propertyId: string, label: string, values: CatalogValue[]): void => {
  const present = values.filter((value) => value.text)
  if (present.length === 0) return
  rows.push({ propertyId, label, values: present })
}

const pushTextValues = (rows: CatalogRow[], propertyId: string, label: string, texts: string[]): void => {
  pushValues(rows, propertyId, label, texts.filter(Boolean).map((text) => ({ text })))
}

const pushBlocks = (rows: CatalogRow[], propertyId: string, label: string, blocks: CatalogBlock[]): void => {
  const present = blocks.filter((block) => block.name || block.meta || block.text)
  if (present.length === 0) return
  rows.push({ propertyId, label, blocks: present })
}

const buildRows = (facts: CatalogFacts, locale: CatalogLocale, translate?: CatalogTranslate): CatalogRow[] => {
  const label = (propertyId: string): string => rowLabel(propertyId, locale, translate)
  const rows: CatalogRow[] = []

  pushValues(rows, 'set', label('set'), facts.sets)
  if (facts.number) pushTextValues(rows, 'number', label('number'), [facts.number])
  pushValues(rows, 'rarity', label('rarity'), facts.rarities)
  if (facts.regulation) pushTextValues(rows, 'regulation', label('regulation'), [facts.regulation])
  if (facts.supertype) pushTextValues(rows, 'supertype', label('supertype'), [facts.supertype])
  pushValues(rows, 'subtype', label('subtype'), facts.subtypes)
  pushValues(rows, 'type', label('type'), facts.types)
  if (facts.hp) pushTextValues(rows, 'hp', label('hp'), [facts.hp])
  if (facts.level) pushTextValues(rows, 'level', label('level'), [facts.level])
  pushValues(rows, 'illustrator', label('illustrator'), facts.illustrators)
  pushValues(rows, 'character', label('character'), facts.characters)
  if (facts.pokedex.length > 0) pushTextValues(rows, 'pokedex', label('pokedex'), [facts.pokedex.join(locale === 'ja' ? '、' : ', ')])
  if (facts.evolvesFrom) pushTextValues(rows, 'evolvesFrom', label('evolvesFrom'), [facts.evolvesFrom])
  pushBlocks(rows, 'attack', label('attack'), facts.attacks.map((attack) => ({
    name: attack.name,
    meta: attackMeta(attack, locale),
    text: attack.text,
  })))
  pushBlocks(rows, 'ability', label('ability'), facts.abilities.map((ability) => ({
    name: ability.name,
    text: ability.text,
  })))
  pushTextValues(rows, 'weakness', label('weakness'), facts.weaknesses)
  pushTextValues(rows, 'resistance', label('resistance'), facts.resistances)
  if (facts.retreat) pushTextValues(rows, 'retreat', label('retreat'), [facts.retreat])
  if (facts.ancientTrait) {
    pushBlocks(rows, 'ancientTrait', label('ancientTrait'), [{
      name: facts.ancientTrait.name,
      text: facts.ancientTrait.text,
    }])
  }
  pushBlocks(rows, 'rule', label('rule'), facts.rules.map((rule) => ({ text: rule })))
  if (facts.flavor) pushBlocks(rows, 'flavorText', label('flavorText'), [{ text: facts.flavor }])
  if (facts.gbPack) pushTextValues(rows, 'gbPack', label('gbPack'), [facts.gbPack])
  if (facts.gb2Pack) pushTextValues(rows, 'gb2Pack', label('gb2Pack'), [facts.gb2Pack])
  if (facts.wiki) pushValues(rows, 'wiki', label('wiki'), [{ text: facts.wiki, href: facts.wiki }])

  return rows
}

const buildCardProductJsonLd = (
  presented: Omit<PresentedCardCatalog, 'jsonLd'>,
  pageUrl?: string,
): Record<string, unknown> | undefined => {
  if (!presented.intro && !presented.description && presented.rows.length === 0) return

  const additionalProperty: Array<Record<string, unknown>> = []
  for (const row of presented.rows) {
    for (const value of row.values ?? []) {
      const entry: Record<string, unknown> = {
        '@type': 'PropertyValue',
        propertyID: row.propertyId,
        name: row.label,
        value: value.text,
      }
      const url = absoluteUrl(pageUrl, value.href)
      if (url) entry.url = url
      additionalProperty.push(entry)
    }
    for (const block of row.blocks ?? []) {
      const value = [block.name, block.meta].filter(Boolean).join(' ')
      if (!value && !block.text) continue
      const entry: Record<string, unknown> = {
        '@type': 'PropertyValue',
        propertyID: row.propertyId,
        name: row.label,
        value: value || block.text,
      }
      if (value && block.text) entry.description = block.text
      additionalProperty.push(entry)
    }
  }

  // #747 should spread Offer onto this object (JPY, InStock only when a listing exists).
  // Do not invent a second additionalProperty list there.
  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: presented.name,
    productID: `holic:${presented.itemId}`,
  }
  const description = presented.intro || presented.description
  if (description) jsonLd.description = description
  if (presented.images.length > 0) jsonLd.image = presented.images
  const url = canonicalPageUrl(pageUrl)
  if (url) jsonLd.url = url
  const number = presented.rows.find((row) => row.propertyId === 'number')?.values?.[0]?.text
  if (number) jsonLd.sku = number
  const sets = presented.rows.find((row) => row.propertyId === 'set')?.values?.map((value) => value.text) ?? []
  if (sets.length > 0) jsonLd.category = sets.join(', ')
  if (additionalProperty.length > 0) jsonLd.additionalProperty = additionalProperty
  return jsonLd
}

const presentCardCatalog = (
  source: CardCatalogSource,
  locale: CatalogLocale,
  options: PresentCardCatalogOptions = {},
): PresentedCardCatalog => {
  const facts = collectFacts(source, locale, options.translate)
  const rows = buildRows(facts, locale, options.translate)
  const presented: PresentedCardCatalog = {
    itemId: facts.itemId,
    name: facts.name,
    heading: rowLabel('title', locale, options.translate),
    separator: locale === 'ja' ? '、' : ', ',
    description: facts.description,
    intro: buildIntro(facts, locale),
    rows,
    images: facts.images,
  }
  presented.jsonLd = buildCardProductJsonLd(presented, options.pageUrl)
  return presented
}

const cardCatalogIsVisible = (presented: PresentedCardCatalog): boolean => {
  return Boolean(presented.description || presented.intro || presented.rows.length > 0)
}

const escapeHtml = (value: string): string => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

const renderValuesHtml = (values: CatalogValue[], separator: string): string => {
  return values.map((value, index) => {
    const text = escapeHtml(value.text)
    const prefix = index > 0 ? escapeHtml(separator) : ''
    const href = safeHref(value.href)
    if (!href) return prefix + text
    if (href.startsWith('/')) return `${prefix}<a href="${escapeHtml(href)}">${text}</a>`
    return `${prefix}<a href="${escapeHtml(href)}" rel="noreferrer">${text}</a>`
  }).join('')
}

const renderBlocksHtml = (blocks: CatalogBlock[]): string => {
  const items = blocks.map((block) => {
    const name = block.name ? `<span class="card-catalog-attack-name">${escapeHtml(block.name)}</span>` : ''
    const meta = block.meta ? `<span class="card-catalog-attack-cost">${escapeHtml(block.meta)}</span>` : ''
    const text = block.text ? `<p class="card-catalog-prose">${escapeHtml(block.text)}</p>` : ''
    return `<li>${name}${meta}${text}</li>`
  }).join('')
  return `<ul class="card-catalog-attacks">${items}</ul>`
}

const jsonLdScript = (jsonLd: Record<string, unknown>): string => {
  const payload = JSON.stringify(jsonLd).replace(/</g, '\\u003c')
  return `<script type="application/ld+json">${payload}</script>`
}

const renderCardCatalogHtml = (presented: PresentedCardCatalog): string => {
  if (!cardCatalogIsVisible(presented)) return ''

  const description = presented.description
    ? `<p class="card-catalog-description">${escapeHtml(presented.description)}</p>`
    : ''
  const intro = presented.intro
    ? `<p class="card-catalog-intro">${escapeHtml(presented.intro)}</p>`
    : ''
  const rows = presented.rows.map((row) => {
    const body = row.values
      ? renderValuesHtml(row.values, presented.separator)
      : renderBlocksHtml(row.blocks ?? [])
    return `<div class="card-catalog-row" data-catalog-property="${escapeHtml(row.propertyId)}"><dt>${escapeHtml(row.label)}</dt><dd>${body}</dd></div>`
  }).join('')
  const list = rows ? `<dl>${rows}</dl>` : ''
  const script = presented.jsonLd ? jsonLdScript(presented.jsonLd) : ''

  return `<section class="card-catalog" id="card-catalog" aria-labelledby="card-catalog-heading"><h2 id="card-catalog-heading">${escapeHtml(presented.heading)}</h2>${description}${intro}${list}${script}</section>`
}

export {
  buildCardProductJsonLd,
  cardCatalogIsVisible,
  catalogLocale,
  presentCardCatalog,
  renderCardCatalogHtml,
}
